import React, { useState, useEffect } from "react";
import {
  Paper,
  Typography,
  Box,
  Stack,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Chip,
  Avatar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Dialog,
  DialogContent,
  Zoom,
} from "@mui/material";
import { Grid } from "@mui/material";
import { styled, keyframes } from "@mui/system";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import CloseIcon from "@mui/icons-material/Close";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CallReceivedIcon from "@mui/icons-material/CallReceived";
import OutboxIcon from "@mui/icons-material/Outbox";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useTranslation } from "react-i18next";
import CInput2 from "../../../basecomp/components/CInput2";
import CButton from "../../../basecomp/components/CButton2";
import CBackDrop from "../../../basecomp/components/CBackDrop";
import useFullscreen, {
  isFullscreenSupported,
  isTouchDevice,
} from "../../../basecomp/base/useFullscreen";
import useKeyboardUnlock from "../../../basecomp/base/useKeyboardUnlock";
import IssueReceiveVm from "../viewmodel/IssueReceiveVm";
import MaterialDetailModal from "./MaterialDetailModal";

const BLUE = "#052660";
const GREEN = "#3CB043";
// Vivid, solid fills (not pastel washes) so the row content is white text
// on a rich blue/green — dark enough for proper white-text contrast, still
// clearly reading as blue vs. green.
const BLUE_BG = "#0D47A1";
const BLUE_BG_HOVER = "#0B3D91";
const GREEN_BG = "#2E7D32";
const GREEN_BG_HOVER = "#256628";
const rowColor = (isReceive) => ({
  accent: isReceive ? BLUE : GREEN,
  bg: isReceive ? BLUE_BG : GREEN_BG,
  bgHover: isReceive ? BLUE_BG_HOVER : GREEN_BG_HOVER,
});

// Gentle attention-drawing pulse on the "?" icon in the document-type
// chooser popup — subtle enough not to be distracting while the user reads.
const pulseAnim = keyframes`
  0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(199, 119, 0, 0.25); }
  50% { transform: scale(1.08); box-shadow: 0 0 0 10px rgba(199, 119, 0, 0); }
`;

const focusField = (id) => {
  const el = document.getElementById(id);
  if (el) {
    el.focus();
  }
};

// Blank/missing values read as "-" everywhere in the history tables instead
// of an empty cell, so it's clear the data wasn't left out by accident.
const orDash = (value) =>
  value === null || value === undefined || value === "" ? "-" : value;
// 76

const MONTHS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const modernSkinSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "#f8fafc",
    transition: "box-shadow .18s ease, background-color .18s ease",
  },
  "& .MuiOutlinedInput-root.Mui-focused": {
    backgroundColor: "#ffffff",
    boxShadow: `0 0 0 4px ${BLUE}1f`,
  },
  "& .MuiButton-root": {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    boxShadow: "none",
    paddingInline: "1.5em",
    paddingBlock: "0.6em",
  },
};

const PageBg = styled(Box)({
  minHeight: "100%",
  background: "linear-gradient(180deg, #f5f7fb 0%, #eef1f6 100%)",
  padding: "5em 1.5em 3em 1.5em",
});

const PageContent = styled(Box)({
  maxWidth: "1400px",
  margin: "0 auto",
});

const Card = styled(Paper)({
  borderRadius: "1.25em",
  padding: "2em",
  marginBottom: "1.75em",
  boxShadow: "0 8px 28px rgba(15, 23, 42, 0.07)",
  border: "1px solid rgba(15, 23, 42, 0.05)",
});

const IssueReceiveLanding = () => {
  const { t } = useTranslation();
  const {
    docNumber,
    setDocNumber,
    handleDocSubmit,
    showTypeDialog,
    confirmReceive,
    confirmIssue,
    dismissTypeDialog,
    loading,
    years,
    selectedYear,
    handleYearChange,
    selectedMonth,
    handleMonthChange,
    combinedList,
    searchTerm,
    setSearchTerm,
    handleExport,
    handleExportDetail,
    historyView,
    selectedDoc,
    detailList,
    viewDetail,
    backToSummary,
    setMaterialNo,
    materialNo,
    handelMaterialSearch,
    materialData,
    locationData,
    showMaterialModal,
    setShowMaterialModal,
  } = IssueReceiveVm();

  const { isFullscreen, toggleFullscreen, droppedUnexpectedly, resumeFullscreen } = useFullscreen();
  const touchDevice = isTouchDevice();
  const { getFieldProps, getResetKey, unlockField } = useKeyboardUnlock(touchDevice);

  // Every time this page is landed on, focus goes straight to the PO/Work
  // Order field so scanning/typing can start immediately. Deferred a tick —
  // right on mount (e.g. arriving via a route transition) the field can be
  // present in the DOM but not yet actually focusable for a frame, which
  // would otherwise silently no-op.
  useEffect(() => {
    const timer = setTimeout(() => focusField("doc_number_input"), 0);
    return () => clearTimeout(timer);
  }, []);

  // Re-focus the PO/Work Order field whenever fullscreen is toggled, in
  // either direction — the fullscreen transition itself steals focus
  // (browser default: entering can leave nothing focused, exiting tends to
  // leave focus sitting on the toggle button instead), so without this a
  // scan/keystroke right after toggling fullscreen would land nowhere.
  // Deferred a tick for the same reason as the mount-focus effect above —
  // the transition itself needs to finish before the field is focusable.
  useEffect(() => {
    const timer = setTimeout(() => focusField("doc_number_input"), 0);
    return () => clearTimeout(timer);
  }, [isFullscreen]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  useEffect(() => {
    setPage(0);
  }, [searchTerm, selectedYear, selectedMonth]);
  const paginatedList = combinedList.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const [detailPage, setDetailPage] = useState(0);
  const [detailRowsPerPage, setDetailRowsPerPage] = useState(10);
  useEffect(() => {
    setDetailPage(0);
  }, [selectedDoc]);
  const paginatedDetailList = detailList.slice(
    detailPage * detailRowsPerPage,
    detailPage * detailRowsPerPage + detailRowsPerPage
  );
  const handleChangeDetailPage = (event, newPage) => setDetailPage(newPage);
  const handleChangeDetailRowsPerPage = (event) => {
    setDetailRowsPerPage(parseInt(event.target.value, 10));
    setDetailPage(0);
  };

  return (
    <PageBg>
      <PageContent sx={modernSkinSx}>
        <CBackDrop cOpen={loading} />

        <Dialog
          open={showTypeDialog}
          onClose={dismissTypeDialog}
          TransitionComponent={Zoom}
          transitionDuration={280}
          PaperProps={{
            sx: {
              borderRadius: "1.5em",
              maxWidth: "30em",
              width: "100%",
              overflow: "visible",
              boxShadow: "0 24px 60px rgba(15, 23, 42, 0.25)",
            },
          }}
        >
          <IconButton
            onClick={dismissTypeDialog}
            sx={{
              position: "absolute",
              top: "0.6em",
              right: "0.6em",
              color: "text.secondary",
              backgroundColor: "#f1f4f9",
              "&:hover": { backgroundColor: "#e2e8f0" },
            }}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          <DialogContent sx={{ textAlign: "center", padding: "2.5em 2em 2em" }}>
            <Avatar
              sx={{
                width: 64,
                height: 64,
                margin: "0 auto 1em",
                bgcolor: "#fff4e5",
                color: "#c77700",
                animation: `${pulseAnim} 1.8s ease-in-out infinite`,
              }}
            >
              <HelpOutlineIcon sx={{ fontSize: 34 }} />
            </Avatar>

            <Typography variant="h6" fontWeight={700} gutterBottom>
              {t("choose_document_type_title")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ marginBottom: "0.4em" }}>
              {t("choose_document_type_desc")}
            </Typography>
            {docNumber && (
              <Box
                sx={{
                  display: "inline-block",
                  backgroundColor: "#f1f4f9",
                  color: "#334155",
                  fontWeight: 700,
                  borderRadius: "0.6em",
                  padding: "0.25em 0.9em",
                  marginTop: "0.5em",
                  marginBottom: "1.75em",
                  letterSpacing: ".02em",
                }}
              >
                {docNumber}
              </Box>
            )}

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.75} sx={{ marginTop: docNumber ? 0 : "1.75em" }}>
              <Box
                onClick={confirmReceive}
                sx={{
                  flex: 1,
                  cursor: "pointer",
                  borderRadius: "1.1em",
                  border: `2px solid ${BLUE}26`,
                  padding: "1.4em 1em",
                  transition: "all .2s ease",
                  "&:hover": {
                    borderColor: BLUE,
                    backgroundColor: `${BLUE}0d`,
                    transform: "translateY(-4px)",
                    boxShadow: `0 14px 28px ${BLUE}26`,
                  },
                  "&:hover .arrow-hint": { opacity: 1, transform: "translateX(0)" },
                }}
              >
                <Avatar sx={{ bgcolor: `${BLUE}1a`, color: BLUE, width: 52, height: 52, margin: "0 auto 0.7em" }}>
                  <CallReceivedIcon />
                </Avatar>
                <Typography fontWeight={700} sx={{ color: BLUE }}>
                  {t("receive_material")}
                </Typography>
                <Stack
                  direction="row"
                  spacing={0.4}
                  justifyContent="center"
                  alignItems="center"
                  className="arrow-hint"
                  sx={{
                    color: BLUE,
                    opacity: 0,
                    transform: "translateX(-4px)",
                    transition: "all .2s ease",
                    marginTop: "0.3em",
                  }}
                >
                  <Typography variant="caption" fontWeight={700}>
                    {t("go")}
                  </Typography>
                  <ArrowForwardIcon sx={{ fontSize: 14 }} />
                </Stack>
              </Box>

              <Box
                onClick={confirmIssue}
                sx={{
                  flex: 1,
                  cursor: "pointer",
                  borderRadius: "1.1em",
                  border: `2px solid ${GREEN}26`,
                  padding: "1.4em 1em",
                  transition: "all .2s ease",
                  "&:hover": {
                    borderColor: GREEN,
                    backgroundColor: `${GREEN}0d`,
                    transform: "translateY(-4px)",
                    boxShadow: `0 14px 28px ${GREEN}26`,
                  },
                  "&:hover .arrow-hint": { opacity: 1, transform: "translateX(0)" },
                }}
              >
                <Avatar sx={{ bgcolor: `${GREEN}1a`, color: GREEN, width: 52, height: 52, margin: "0 auto 0.7em" }}>
                  <OutboxIcon />
                </Avatar>
                <Typography fontWeight={700} sx={{ color: GREEN }}>
                  {t("issue_material")}
                </Typography>
                <Stack
                  direction="row"
                  spacing={0.4}
                  justifyContent="center"
                  alignItems="center"
                  className="arrow-hint"
                  sx={{
                    color: GREEN,
                    opacity: 0,
                    transform: "translateX(-4px)",
                    transition: "all .2s ease",
                    marginTop: "0.3em",
                  }}
                >
                  <Typography variant="caption" fontWeight={700}>
                    {t("go")}
                  </Typography>
                  <ArrowForwardIcon sx={{ fontSize: 14 }} />
                </Stack>
              </Box>
            </Stack>
          </DialogContent>
        </Dialog>

<Card sx={{ p: 3, mb: 3 }}>
  <Grid container spacing={3}>
    {/* Document Search */}
    <Grid item xs={12} md={6}>
      <Stack
        spacing={2}
        sx={{
          height: "100%",
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{
            minHeight: { xs: "auto", md: 90 },
          }}
        >
          <Avatar
            sx={{
              bgcolor: `${BLUE}1f`,
              color: BLUE,
              width: 48,
              height: 48,
              flexShrink: 0,
            }}
          >
            <SwapHorizIcon />
          </Avatar>

          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h5" fontWeight={700} lineHeight={1.2}>
              {t("issue_receive")}
            </Typography>

            <Typography variant="body2" color="text.secondary">
              {t("issue_receive_input_desc")}
            </Typography>
          </Box>
        </Stack>

        <CInput2
          cId="doc_number_input"
          cHintText={t("issue_receive_input_label")}
          cValue={docNumber}
          cOnChange={setDocNumber}
          cHandleSubmit={handleDocSubmit}
          cHtmlProps={getFieldProps("doc_number_input")}
          cKeyReset={getResetKey("doc_number_input")}
          cOnUnlock={() => unlockField("doc_number_input")}
          cFullWidth
          cEndAdornment={
            <Tooltip title={t("go")}>
              <IconButton
                onClick={handleDocSubmit}
                sx={{
                  backgroundColor: BLUE,
                  color: "#fff",
                  "&:hover": {
                    backgroundColor: `${BLUE}dd`,
                  },
                }}
              >
                <SearchIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          }
        />
      </Stack>
    </Grid>

    {/* Material Search */}
    <Grid item xs={12} md={6}>
      <Stack
        spacing={2}
        sx={{
          height: "100%",
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{
            minHeight: { xs: "auto", md: 90 },
          }}
        >
          <Avatar
            sx={{
              bgcolor: `${BLUE}1f`,
              color: BLUE,
              width: 48,
              height: 48,
              flexShrink: 0,
            }}
          >
            <SwapHorizIcon />
          </Avatar>

          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h5" fontWeight={700} lineHeight={1.2}>
              {t("Material Search")}
            </Typography>

            <Typography variant="body2" color="text.secondary">
              {t("Enter Material number to get details")}
            </Typography>
          </Box>
        </Stack>

        <CInput2
          cId="material_search_input"
          cHintText={t("Enter Material Number")}
          cValue={materialNo}
          cOnChange={setMaterialNo}
          cHandleSubmit={handelMaterialSearch}
          cHtmlProps={getFieldProps("material_search_input")}
          cKeyReset={getResetKey("material_search_input")}
          cOnUnlock={() => unlockField("material_search_input")}
          cFullWidth
          cEndAdornment={
            <Tooltip title={t("go")}>
              <IconButton
                onClick={handelMaterialSearch}
                sx={{
                  backgroundColor: BLUE,
                  color: "#fff",
                  "&:hover": {
                    backgroundColor: `${BLUE}dd`,
                  },
                }}
              >
                <SearchIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          }
        />
      </Stack>
    </Grid>
  </Grid>
</Card>

        <Card sx={{ marginBottom: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ marginBottom: "0.5em" }}>
            <Stack direction="row" spacing={1} alignItems="center">
              {historyView === "detail" && (
                <Tooltip title={t("back_to_summary")}>
                  <IconButton onClick={backToSummary} size="small">
                    <ArrowBackIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Typography variant="h6" fontWeight={700} gutterBottom sx={{ marginBottom: 0 }}>
                {historyView === "detail"
                  ? `${t("history")} — ${selectedDoc?.document_no || ""}`
                  : t("history")}
              </Typography>
            </Stack>
            {historyView === "detail" && (
              <CButton
                cText={t("export_data")}
                cSrc={<DownloadIcon />}
                cTooltipText="export_data"
                cOnClick={handleExportDetail}
              />
            )}
          </Stack>

          {historyView === "summary" && (
            <>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.5}
                alignItems={{ xs: "stretch", md: "center" }}
                justifyContent="space-between"
                sx={{ marginBottom: "1.5em" }}
              >
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "stretch", sm: "center" }} flex={1}>
                  <FormControl size="small" sx={{ minWidth: "8em" }}>
                    <InputLabel id="year-select-label">{t("year")}</InputLabel>
                    <Select
                      labelId="year-select-label"
                      label={t("year")}
                      value={selectedYear || ""}
                      onChange={(e) => handleYearChange(e.target.value)}
                    >
                      {years.map((year) => (
                        <MenuItem key={year} value={year}>
                          {year}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: "10em" }}>
                    <InputLabel id="month-select-label">{t("month")}</InputLabel>
                    <Select
                      labelId="month-select-label"
                      label={t("month")}
                      value={selectedMonth}
                      onChange={(e) => handleMonthChange(e.target.value)}
                    >
                      <MenuItem value="">{t("all_months")}</MenuItem>
                      {MONTHS.map((m) => (
                        <MenuItem key={m.value} value={m.value}>
                          {m.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Box sx={{ width: "13em", flexShrink: 0 }}>
                    <CInput2
                      cId="history_search_input"
                      cHintText={t("search_history")}
                      cValue={searchTerm}
                      cOnChange={setSearchTerm}
                      cFullWidth
                      cIsSmall
                      cHelperText={null}
                      cHtmlProps={getFieldProps("history_search_input")}
                      cKeyReset={getResetKey("history_search_input")}
                      cOnUnlock={() => unlockField("history_search_input")}
                    />
                  </Box>
                </Stack>
                <CButton
                  cText={t("export_data")}
                  cSrc={<DownloadIcon />}
                  cTooltipText="export_data"
                  cOnClick={handleExport}
                />
              </Stack>

              <Box
                sx={{
                  borderRadius: "0.9em",
                  overflowX: "auto",
                  overflowY: "auto",
                  maxHeight: "50vh",
                  minHeight: "22em",
                  border: "1px solid #e2e8f0",
                }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ "& th": { backgroundColor: "#f1f4f9", fontWeight: 700, fontSize: ".8em", whiteSpace: "nowrap" } }}>
                      <TableCell>{t("document_number")}</TableCell>
                      <TableCell>{t("item_count")}</TableCell>
                      <TableCell>{t("delivered_by")}</TableCell>
                      <TableCell>{t("received_by")}</TableCell>
                      <TableCell>{t("issued_to")}</TableCell>
                      <TableCell>{t("collected_by")}</TableCell>
                      <TableCell align="center">{t("view")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedList.map((row, idx) => {
                      const isReceive = row.doc_type === "RECEIVE";
                      const { accent, bg, bgHover } = rowColor(isReceive);
                      // Signed the same way as the per-line Qty in the detail
                      // table below: Issue removes stock (-10), Receive adds
                      // it (10).
                      const itemCount = Number(row.item_count) || 0;
                      const itemCountDisplay = isReceive ? itemCount : -itemCount;
                      return (
                        <TableRow
                          key={idx}
                          sx={{
                            borderLeft: `6px solid ${accent}`,
                            backgroundColor: bg,
                            "&:hover": { backgroundColor: bgHover },
                            "& .MuiTableCell-root": { color: "#fff" },
                          }}
                        >
                          <TableCell sx={{ fontWeight: 700 }}>{row.document_no}</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{itemCountDisplay}</TableCell>
                          <TableCell>{orDash(row.delivered_by)}</TableCell>
                          <TableCell>{orDash(row.received_by)}</TableCell>
                          <TableCell>{orDash(row.issued_to)}</TableCell>
                          <TableCell>{orDash(row.collected_by)}</TableCell>
                          <TableCell align="center">
                            <Tooltip title={t("view")}>
                              <IconButton size="small" onClick={() => viewDetail(row)}>
                                <VisibilityIcon fontSize="small" sx={{ color: "#fff" }} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {combinedList.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} sx={{ textAlign: "center" }}>
                          {t("no_data")}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
              <TablePagination
                component="div"
                count={combinedList.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </>
          )}

          {historyView === "detail" && (
            <>
              {selectedDoc && (
                <Box
                  sx={{
                    borderRadius: "0.9em",
                    padding: "1.25em 1.5em",
                    marginBottom: "1.25em",
                    backgroundColor: rowColor(selectedDoc.doc_type === "RECEIVE").bg,
                  }}
                >
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 1, sm: 4 }} flexWrap="wrap">
                    <Box>
                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.75)" }}>{t("document_number")}</Typography>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ color: "#fff" }}>{selectedDoc.document_no}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.75)" }}>{t("item_count")}</Typography>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ color: "#fff" }}>
                        {(() => {
                          const count = Number(selectedDoc.item_count) || 0;
                          return selectedDoc.doc_type === "RECEIVE" ? count : -count;
                        })()}
                      </Typography>
                    </Box>
                    {selectedDoc.delivered_by && (
                      <Box>
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.75)" }}>{t("delivered_by")}</Typography>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ color: "#fff" }}>{selectedDoc.delivered_by}</Typography>
                      </Box>
                    )}
                    {selectedDoc.received_by && (
                      <Box>
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.75)" }}>{t("received_by")}</Typography>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ color: "#fff" }}>{selectedDoc.received_by}</Typography>
                      </Box>
                    )}
                    {selectedDoc.issued_to && (
                      <Box>
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.75)" }}>{t("issued_to")}</Typography>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ color: "#fff" }}>{selectedDoc.issued_to}</Typography>
                      </Box>
                    )}
                    {selectedDoc.collected_by && (
                      <Box>
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.75)" }}>{t("collected_by")}</Typography>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ color: "#fff" }}>{selectedDoc.collected_by}</Typography>
                      </Box>
                    )}
                    {selectedDoc.signature && (
                      <Box>
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.75)" }}>{t("signature")}</Typography>
                        <Box>
                          <img
                            src={selectedDoc.signature}
                            alt={t("signature")}
                            style={{ height: "40px", borderRadius: "4px", background: "#fff", display: "block" }}
                          />
                        </Box>
                      </Box>
                    )}
                  </Stack>
                </Box>
              )}

              <Box
                sx={{
                  borderRadius: "0.9em",
                  overflowX: "auto",
                  overflowY: "auto",
                  maxHeight: "50vh",
                  minHeight: "18em",
                  border: "1px solid #e2e8f0",
                }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ "& th": { backgroundColor: "#f1f4f9", fontWeight: 700, fontSize: ".8em", whiteSpace: "nowrap" } }}>
                      <TableCell>{t("material_number")}</TableCell>
                      <TableCell>{t("material_description")}</TableCell>
                      <TableCell>{t("uom")}</TableCell>
                      <TableCell>{t("quantity")}</TableCell>
                      <TableCell>{t("storage_location")}</TableCell>
                      <TableCell>{t("storage_bin")}</TableCell>
                      <TableCell>{t("delivered_by")}</TableCell>
                      <TableCell>{t("received_by")}</TableCell>
                      <TableCell>{t("issued_to")}</TableCell>
                      <TableCell>{t("collected_by")}</TableCell>
                      <TableCell>{t("date")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedDetailList.map((row, idx) => {
                      const isReceive = selectedDoc?.doc_type === "RECEIVE";
                      const { accent, bg, bgHover } = rowColor(isReceive);
                      const qtyValue = Number(row.qty) || 0;
                      // Work Order (Issue) quantities show as negative — a
                      // material moving out reads as a negative movement,
                      // Receive quantities stay positive.
                      const qtyDisplay = isReceive ? qtyValue : -qtyValue;
                      const dateFormatted = row.created_at
                        ? new Date(Number(row.created_at) * 1000).toLocaleString()
                        : "";
                      return (
                        <TableRow
                          key={idx}
                          sx={{
                            borderLeft: `6px solid ${accent}`,
                            backgroundColor: bg,
                            "&:hover": { backgroundColor: bgHover },
                            "& .MuiTableCell-root": { color: "#fff" },
                          }}
                        >
                          <TableCell sx={{ fontWeight: 600 }}>{row.material_no}</TableCell>
                          <TableCell>{orDash(row.material_desc)}</TableCell>
                          <TableCell>{orDash(row.uom)}</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{qtyDisplay}</TableCell>
                          <TableCell>{orDash(row.storage_loc)}</TableCell>
                          <TableCell>{orDash(row.storage_bin)}</TableCell>
                          <TableCell>{orDash(row.delivered_by)}</TableCell>
                          <TableCell>{orDash(row.received_by)}</TableCell>
                          <TableCell>{orDash(row.issued_to)}</TableCell>
                          <TableCell>{orDash(row.collected_by)}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>{orDash(dateFormatted)}</TableCell>
                        </TableRow>
                      );
                    })}
                    {detailList.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={11} sx={{ textAlign: "center" }}>
                          {t("no_data")}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
              <TablePagination
                component="div"
                count={detailList.length}
                page={detailPage}
                onPageChange={handleChangeDetailPage}
                rowsPerPage={detailRowsPerPage}
                onRowsPerPageChange={handleChangeDetailRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </>
          )}
        </Card>
      </PageContent>

      <MaterialDetailModal
  open={showMaterialModal}
  onClose={() => {setShowMaterialModal(false);setMaterialNo("")}}
  materialData={materialData}
  locationData={locationData}
/>
      {isFullscreenSupported() && droppedUnexpectedly && (
        <Chip
          label={t("tap_to_resume_fullscreen")}
          onClick={resumeFullscreen}
          sx={{
            position: "fixed",
            bottom: "5.5em",
            right: "1.5em",
            zIndex: 1400,
            backgroundColor: BLUE,
            color: "#fff",
            fontWeight: 600,
            cursor: "pointer",
            boxShadow: "0 8px 20px rgba(15, 23, 42, 0.3)",
          }}
        />
      )}

      {isFullscreenSupported() && (
        <Tooltip title={t(isFullscreen ? "exit_full_screen" : "full_screen")}>
          <IconButton
            onClick={toggleFullscreen}
            sx={{
              position: "fixed",
              bottom: "1.5em",
              right: "1.5em",
              zIndex: 1400,
              width: 52,
              height: 52,
              borderRadius: "14px",
              backgroundColor: "#0f172a",
              color: "#22c55e",
              boxShadow: "0 8px 20px rgba(15, 23, 42, 0.35)",
              "&:hover": { backgroundColor: "#1e293b" },
            }}
          >
            {isFullscreen ? <CloseFullscreenIcon /> : <OpenInFullIcon />}
          </IconButton>
        </Tooltip>
      )}
    </PageBg>
  );
};

export default IssueReceiveLanding;
